import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiError } from '@/types';
import { firebaseApiClient } from './firebaseApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        const apiError: ApiError = {
          message: error.response?.data?.message || 'An error occurred',
          status: error.response?.status || 500,
          field: error.response?.data?.field,
        };

        // If token is expired, clear it and redirect to login
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
        }

        return Promise.reject(apiError);
      }
    );
  }

  // Auth endpoints - Using Firebase
  async login(email: string, password: string) {
    return firebaseApiClient.login(email, password);
  }

  async register(data: any) {
    return firebaseApiClient.register(data);
  }

  async logout() {
    return firebaseApiClient.logout();
  }

  async getCurrentUser() {
    return firebaseApiClient.getCurrentUser();
  }

  async sendPasswordReset(email: string) {
    return firebaseApiClient.sendPasswordReset(email);
  }

  // Organizations
  async getOrganization(id: string) {
    const response = await this.client.get(`/organizations/${id}`);
    return response.data;
  }

  async updateOrganization(id: string, data: any) {
    const response = await this.client.put(`/organizations/${id}`, data);
    return response.data;
  }

  // Forms - Using Firebase
  async getForms() {
    return firebaseApiClient.getForms();
  }

  async getForm(id: string) {
    return firebaseApiClient.getForm(id);
  }

  async createForm(data: any) {
    return firebaseApiClient.createForm(data);
  }

  async updateForm(id: string, data: any) {
    return firebaseApiClient.updateForm(id, data);
  }

  async deleteForm(id: string) {
    return firebaseApiClient.deleteForm(id);
  }

  async duplicateForm(id: string) {
    const response = await this.client.post(`/forms/${id}/duplicate`);
    return response.data;
  }

  // Public form endpoints - Using Firebase
  async getPublicForm(publicLink: string) {
    return firebaseApiClient.getPublicForm(publicLink);
  }

  async submitPublicForm(formId: string, data: any) {
    return firebaseApiClient.createSubmission(formId, data);
  }

  // Submissions - Using Firebase
  async getSubmissions(filters?: any) {
    return firebaseApiClient.getSubmissions(filters?.formId);
  }

  async getSubmission(id: string) {
    const response = await this.client.get(`/submissions/${id}`);
    return response.data;
  }

  async downloadSubmissionDocument(id: string) {
    const response = await this.client.get(`/submissions/${id}/document`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Dashboard
  async getDashboardStats() {
    const response = await this.client.get('/dashboard/stats');
    return response.data;
  }

  async getRecentActivity() {
    const response = await this.client.get('/dashboard/recent');
    return response.data;
  }

  // File upload
  async uploadWaiverPdf(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.client.post('/upload/waiver-pdf', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Email verification - Firebase-based implementation
  async sendVerificationEmail(email: string) {
    return firebaseApiClient.sendEmailVerification(email);
  }

  async checkEmailVerification() {
    return firebaseApiClient.checkEmailVerification();
  }

  async resendVerificationEmail(email: string) {
    return firebaseApiClient.sendEmailVerification(email);
  }
}

export const apiClient = new ApiClient();
export default apiClient;